export type ProductType = 'notes' | 'pyq' | 'quiz' | 'test-series' | 'bundle';

export type Product = {
  id: string;
  type: ProductType;
  title: string;
  subtitle: string;
  exam: string;
  subject: string;
  author: string;
  price: number;
  mrp: number;
  rating: number;
  ratingCount: number;
  students: number;
  emoji: string;
  gradient: string[];
  bestseller?: boolean;
  isFree: boolean;
  category?: string | null;
  tags: string[];
  description: string;
  features: string[];
  contents: { title: string; meta: string; hasPdf?: boolean }[];
};

export type Category = {
  id: string;
  type: ProductType;
  title: string;
  tagline: string;
  description: string;
};

export type Exam = { id: string; name: string; emoji: string; color: string };

export type Banner = {
  id: string;
  title: string;
  subtitle: string;
  cta: string;
  gradient: string[];
  emoji: string;
  productId: string;
};

export type User = { name: string; email: string; phone: string | null };

export const TypeTheme: Record<ProductType, { label: string; color: string; gradient: string[]; icon: string }> = {
  notes: { label: 'Notes', color: '#5A2D9C', gradient: ['#7C4DBE', '#5A2D9C'], icon: '📝' },
  pyq: { label: 'PYQs', color: '#2563EB', gradient: ['#4F86F7', '#2563EB'], icon: '🕘' },
  quiz: { label: 'Quizzes', color: '#0E9AA5', gradient: ['#22C7C7', '#0E9AA5'], icon: '❓' },
  'test-series': { label: 'Test Series', color: '#F0501E', gradient: ['#FF8A5B', '#F0501E'], icon: '🏆' },
  bundle: { label: 'Bundle', color: '#B8860B', gradient: ['#F5C451', '#B8860B'], icon: '📦' },
};

export function discountPercent(p: Product): number {
  if (!p.mrp || p.mrp <= p.price) return 0;
  return Math.round(((p.mrp - p.price) / p.mrp) * 100);
}

export function formatCount(n: number): string {
  if (n >= 100000) return `${(n / 100000).toFixed(1)}L`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return `${n}`;
}

/**
 * Bundles first, everything else in its existing order.
 * Applied once where the catalog loads, so every screen that lists products
 * (store, home, category, search) shows the bundle at the top without each
 * one having to sort for itself.
 */
export function bundlesFirst<T extends { type: ProductType }>(products: T[]): T[] {
  return [...products].sort((a, b) => Number(b.type === 'bundle') - Number(a.type === 'bundle'));
}
